import { select, selectAll } from "@fruugo/utilities";
import initReadMore from "./read-more.js";
import { i18n } from "./i18n";
import { SafetyAttributeResponse, SafetyAttributes } from "../types";
import { getSafetyAttributes, getTranslatedSafetyAttributes } from "./api";
import { sendClickEvent } from "./analytics/google-analytics";

declare let window: ProductPage;

const {
  machineTranslationRequirements,
  skuId,
  productId,
  currentLang,
  descriptionBeforeTranslation,
  skuInfo,
} = window;

let originalDescriptionToggled = false;

export function initProductTranslations() {
  // On-the-fly machine translations
  if (machineTranslationRequirements.title) {
    translateProductTitle();
  }

  if (machineTranslationRequirements.description) {
    translateProductVariants();
    translateProductDescription();
  }

  if (machineTranslationRequirements.safetyAttributes) {
    storeInitialSafetyAttributes();
    getTranslatedSafetyAttributes(productId, skuId)
      .then((data: SafetyAttributeResponse) => {
        sessionStorage.setItem(
          "translatedSafetyAttributes",
          JSON.stringify(data.attributes)
        );
        updateSafetyAttributes(data.attributes);
      })
      .catch(() => {
        // Don't log this to rollbar as we only support certain countries in the controller
      });
  }

  // If we need to machine translate the product description or safety attributes on-the-fly, or if they have already been machine translated and saved to Solr,
  // add the "This description has been machine translated. View original version" section
  if (
    machineTranslationRequirements.description ||
    machineTranslationRequirements.safetyAttributes ||
    descriptionBeforeTranslation.enabled
  ) {
    initViewOriginalDescription();
  }
}

function storeInitialSafetyAttributes() {
  const attributes = selectAll(".js-safety-attributes .attribute-list");

  const initialAttributes: SafetyAttributes = {};

  attributes.forEach((attribute) => {
    const title = attribute.querySelector(
      "[data-safety-attribute-title]"
    ) as HTMLElement;
    const values = attribute.querySelectorAll("[data-safety-attribute-value]");
    if (title && values) {
      const id = title.dataset.safetyAttributeTitle;

      if (id) {
        initialAttributes[id] = {
          title: title.textContent ?? "",
          values: Array.from(values).map((value) => value.textContent ?? ""),
        };
      }
    }
  });

  sessionStorage.setItem(
    "initialSafetyAttributes",
    JSON.stringify(initialAttributes)
  );
}

function updateSafetyAttributes(data: SafetyAttributes) {
  Object.keys(data).forEach((key) => {
    const titleElement = select(
      `.js-safety-attribute-title[data-safety-attribute-title="${key}"]`
    );
    const valueSpans = selectAll(`span[data-safety-attribute-value="${key}"]`);

    if (!data[key]) return;

    if (titleElement) {
      titleElement.textContent = data[key].title;
    }

    valueSpans &&
      valueSpans.forEach((span, i) => {
        span.textContent = data[key].values[i];
      });
  });
}

function translateProductDescription() {
  fetch(
    `/marketplace/api/translation/skuDescriptionAndDisplayAttributes/${skuId}?language=${currentLang}`
  )
    .then((res) => res.json())
    .then((data) => {
      const container = select(".js-product-description");
      if (container) {
        sessionStorage.setItem(
          "originalDescription",
          container.textContent ?? ""
        );
        sessionStorage.setItem(
          "machineTranslatedDescription",
          data.description ?? ""
        );
        container.textContent = data.description;
      }
      // Re-init read more on the product description after adding in the translated version
      initReadMore();
    })
    .catch(() => {});
}

function translateProductVariants() {
  fetch(
    `/marketplace/api/translation/skuVariantAttributes/${productId}/${skuId}?language=${currentLang}`
  )
    .then((res) => res.json())
    .then((variantsData) => {
      Object.keys(variantsData).forEach((key) => {
        const attributeContainer = select(
          `.form-group[data-attribute_id="${key}"]`
        );
        Object.keys(variantsData[key].valueTranslationsMap).forEach(
          (mapKey) => {
            // Product Variant Options
            attributeContainer &&
              attributeContainer
                .querySelectorAll("option")
                .forEach(function (element) {
                  if (element.textContent === mapKey) {
                    element.textContent =
                      variantsData[key].valueTranslationsMap[mapKey];
                  }
                });
          }
        );
      });
    })
    .catch(() => {});
}

function translateProductTitle() {
  fetch(
    `/marketplace/api/translation/skuTitle/${skuId}?language=${currentLang}`
  )
    .then((res) => res.json())
    .then((data) => {
      const titleName = data.name.trim();
      const titleSeparatorIndex = document.title.lastIndexOf("|");
      if (titleSeparatorIndex > 0) {
        const productTitle = document.title
          .substring(0, titleSeparatorIndex)
          .trim();
        if (productTitle !== titleName) {
          document.title =
            titleName + document.title.substring(titleSeparatorIndex - 1);
        }
      }
      select('meta[property="og:title"]')?.setAttribute("content", titleName);
      select(".js-product-title")?.setAttribute("title", titleName);
    })
    .catch(() => {});
}

function handleOriginalDescriptionToggle(event: Event) {
  updateLink(event);
  toggleDescription(originalDescriptionToggled);

  if (select(".js-safety-attributes")) {
    toggleSafetyAttributes(originalDescriptionToggled);
  }

  sendOriginalDescriptionGAEvent(originalDescriptionToggled);

  originalDescriptionToggled = !originalDescriptionToggled;
}

function updateLink(event: Event) {
  const button = event.target as HTMLButtonElement;
  button.textContent = originalDescriptionToggled
    ? i18n("product.description.view-original")
    : i18n("product.description.view");
}

async function toggleSafetyAttributes(toggled: boolean) {
  // If we did the on-the-fly translations, use the session storage
  if (machineTranslationRequirements.safetyAttributes) {
    let storedAttributes;

    if (toggled) {
      storedAttributes = sessionStorage.getItem("translatedSafetyAttributes");
    } else {
      storedAttributes = sessionStorage.getItem("initialSafetyAttributes");
    }

    if (!storedAttributes) return;

    const attributes = JSON.parse(storedAttributes);
    updateSafetyAttributes(attributes);
  } else {
    // Otherwise request from the server and update
    const language = toggled
      ? currentLang
      : descriptionBeforeTranslation.language;

    getSafetyAttributes(skuId, language)
      .then((data) => updateSafetyAttributes(data.attributes))
      .catch(() => {});
  }
}

function toggleDescription(toggled: boolean) {
  const container = select(".js-product-description");
  if (!container) return;

  // If we did the on-the-fly translations, we'll have the descriptions stored in sessionStorage
  if (machineTranslationRequirements.description) {
    let storedDescription;

    if (toggled) {
      storedDescription = sessionStorage.getItem(
        "machineTranslatedDescription"
      );
    } else {
      storedDescription = sessionStorage.getItem("originalDescription");
    }

    if (storedDescription) container.textContent = storedDescription;
  } else {
    // otherwise get it from the SkuInfo
    if (toggled) {
      container.textContent = skuInfo.description;
    } else {
      container.textContent = skuInfo.descriptionBeforeTranslation;
    }
  }
}

function initViewOriginalDescription() {
  const container = select<HTMLDivElement>(".js-product-description-container");
  const description = select<HTMLDivElement>(".js-product-description");

  description &&
    container?.insertBefore(viewOriginalDescriptionTemplate(), description);
}

const viewOriginalDescriptionTemplate = () => (
  <>
    <p>
      <span>
        {i18n(
          "product.description.translated",
          descriptionBeforeTranslation.languageText
        )}
      </span>{" "}
      <button
        class="js-view-original-description button-link"
        onClick={handleOriginalDescriptionToggle}
      >
        {i18n("product.description.view-original")}
      </button>
    </p>
    <hr />
  </>
);

function sendOriginalDescriptionGAEvent(toggled: boolean) {
  if (!toggled) {
    sendClickEvent(
      "view_original_description",
      `Original Language: ${descriptionBeforeTranslation.language}`
    );
  } else {
    sendClickEvent(
      "view_machine_translated_description",
      `Original Language: ${descriptionBeforeTranslation.language}`
    );
  }
}
